import { useState, useCallback, useMemo } from 'react';
import useConfig from '@hooks/useConfig';
import { isBoolean, orderBy } from 'lodash';
import { isValidLoanType, isFixedRate, isAdjustableRate } from '@helpers';

export default function useProductTypes(defaultProductTypes = []) {
  const config = useConfig();

  //////////////////////////
  // Product Categories
  //////////////////////////

  const [productCategories, setProductCategories] = useState({
    fixed: isDefaultCategory('fixed', config),
    adjustable: isDefaultCategory('adjustable', config)
  });

  const toggleProductCategory = useCallback((name, isEnabled) => {
    setProductCategories(prevCategories => {
      if (!prevCategories.hasOwnProperty(name)) {
        return prevCategories;
      }

      return {
        ...prevCategories,
        [name]: isBoolean(isEnabled) ? isEnabled : !prevCategories[name]
      };
    });
  }, []);

  //////////////////////////
  // Product Types
  //////////////////////////

  const [productTypes, setProductTypes] = useState(() => {
    return defaultProductTypes
      .map(({ isEnabled, loanType, isDefaultSelection, ...rest }) => {
        loanType = loanType?.toLowerCase().trim();

        if (!isEnabled || !isValidLoanType(loanType)) {
          return null;
        }

        return { loanType, isEnabled: !!isDefaultSelection, ...rest };
      })
      .filter(p => p);
  });

  const toggleProductType = useCallback((id, isEnabled) => {
    setProductTypes((prevProductTypes = []) => {
      const index = prevProductTypes.findIndex(p => id === p.productTypeID);

      if (index < 0) {
        return prevProductTypes;
      }

      const productTypes = prevProductTypes.slice();

      productTypes[index] = {
        ...productTypes[index],
        isEnabled: isBoolean(isEnabled)
          ? isEnabled
          : !productTypes[index].isEnabled
      };

      return productTypes;
    });
  }, []);

  //////////////////////////
  // Combined State
  //////////////////////////

  const { hasEnabledProductTypes, data } = useMemo(() => {
    const fixedProductTypes = [];
    const adjustableProductTypes = [];
    const fixedIsEnabled = !!productCategories.fixed;
    const adjustableIsEnabled = !!productCategories.adjustable;

    let hasEnabledProductTypes = false;

    productTypes.forEach(product => {
      if (isFixedRate(product.loanType)) {
        fixedProductTypes.push(product);

        if (fixedIsEnabled && product.isEnabled) {
          hasEnabledProductTypes = true;
        }
      } else if (isAdjustableRate(product.loanType)) {
        adjustableProductTypes.push(product);

        if (adjustableIsEnabled && product.isEnabled) {
          hasEnabledProductTypes = true;
        }
      }
    });

    return {
      hasEnabledProductTypes,
      data: {
        fixed: {
          isEnabled: fixedIsEnabled,
          products: orderBy(fixedProductTypes, productSort, 'desc')
        },
        adjustable: {
          isEnabled: adjustableIsEnabled,
          products: orderBy(adjustableProductTypes, productSort, 'asc')
        }
      }
    };
  }, [productCategories, productTypes]);

  return {
    productTypes: data,
    hasEnabledProductTypes,
    toggleProductCategory,
    toggleProductType
  };
}

function isDefaultCategory(
  category = '',
  { defaultProductCategory: defaultCategory = 'fixed' }
) {
  category = category.toLowerCase();
  defaultCategory = defaultCategory?.toLowerCase();

  return defaultCategory === 'both' || category === defaultCategory;
}

function productSort({ label = '' }) {
  const num = parseInt(label.replace(/(\d+)([^0-9].*)/, '$1').trim());
  return isNaN(num) ? 0 : num;
}
